import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ArrowDownNarrowWide, ChevronDownIcon, ChevronRight, Plus } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import SortByBuilderItem from "./SortByBuilderItem";
import FilterDropdown, { supportedFieldTypes } from "../filter/FilterDropdown";
import { FilterRuleProps } from "../filter/AdvancedFilter";
import Draggable from "../../../board/dnd/Draggable";
import Container from "../../../board/dnd/Container";
import { ContentTypes } from "../../../constants/action-types";

const supportedFieldTaskView = ["assignees", "finish", "start"];

const supportedFieldNoteView = ["start"];

const supportedFieldEmailView = ["start"];

export type SortType = {
  field: string;
  direction: string;
};

interface SortByProps {
  contentType: string;
  appView: string;
  sorts: any;
  onChangeSort: (sorts: SortType[]) => void;
}

const SortBy = ({ contentType, appView, sorts, onChangeSort }: SortByProps) => {
  const { t } = useTranslation();

  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));

  const availableFields = useMemo(() => {
    const fieldDatas = fields
      .filter(
        (item) =>
          item.get("formType") == contentType && supportedFieldTypes.includes(item.get("type"))
      )
      .map((item) => ({
        label: item.get("label"),
        value: item.get("id"),
        data: item.toJS(),
      }))
      .toJS();
    if (contentType === ContentTypes.ACTIVITY && appView === "Tasks") {
      return fieldDatas.filter((item) => supportedFieldTaskView.includes(item.value));
    } else if (contentType === ContentTypes.ACTIVITY && appView === "Notes") {
      return fieldDatas.filter((item) => supportedFieldNoteView.includes(item.value));
    } else if (
      contentType === ContentTypes.ACTIVITY &&
      (appView === "Drafts" || appView === "Outbox" || appView === "Tracked")
    ) {
      return fieldDatas.filter((item) => supportedFieldEmailView.includes(item.value));
    }
    return fieldDatas;
  }, [contentType, appView, fields]);

  const availableSortFields = availableFields.map((item) => ({
    ...item,
    value: `data.${item.value}.value`,
  }));

  const [sortList, setSortList] = useState<SortType[]>([]);

  useEffect(() => {
    if (sorts) {
      setSortList(sorts.toJS());
    }
  }, [sorts]);

  const totalSorts = sortList.length;

  const handleAdd = (value: FilterRuleProps) => {
    const existed = sortList.find((item) => item.field === `data.${value.fieldId}.value`);
    if (existed) return;
    const newSort = {
      field: `data.${value.fieldId}.value`,
      direction: "ASC",
    };
    setSortList([...sortList, newSort]);
    onChangeSort([...sortList, newSort]);
  };

  const handleChange = (index: number, value: SortType) => {
    const newSortList = sortList.map((item, idx) => (idx === index ? value : item));
    setSortList([...newSortList]);
    onChangeSort([...newSortList]);
  };

  const handleRemove = (index: number) => {
    const newSortList = sortList.filter((_, idx) => idx !== index);
    setSortList([...newSortList]);
    onChangeSort([...newSortList]);
  };

  const getFieldName = () => {
    if (sortList.length === 0) return;
    const availableField = availableSortFields.find((item) => item.value === sortList[0].field);
    return availableField?.label;
  };

  const getFieldNames = () => {
    if (sortList.length < 1) return;
    const sortFields = sortList.map((item) => item.field);
    sortFields.shift();
    const availableSortFieldsData = availableSortFields.filter((item) =>
      sortFields.includes(item.value)
    );
    return availableSortFieldsData.map((item) => item.label).join(", ");
  };

  const onDrop = (dropResult) => {
    const { removedIndex, addedIndex } = dropResult;
    if (removedIndex === null || addedIndex === null) return;

    const updated = [...sortList];
    const [moved] = updated.splice(removedIndex, 1);
    updated.splice(addedIndex, 0, moved);

    setSortList([...updated]);
    onChangeSort([...updated]);
  };

  const renderPopoverPanel = (closePopover) => {
    if (totalSorts === 0) {
      return (
        <FilterDropdown
          fields={availableFields}
          isFilter={false}
          onClick={(value) => {
            handleAdd(value);
          }}
          onClose={closePopover}
        />
      );
    }
    return (
      <div className="min-w-60">
        <Container
          onDrop={onDrop}
          dragHandleSelector={".drag-handle"}
          nonDragAreaSelector={".no-drag"}
        >
          {sortList.map((item, index) => {
            return (
              <Draggable key={`${item.field}_${index}`}>
                <SortByBuilderItem
                  idx={index}
                  availableSortFields={availableSortFields}
                  sortBy={item}
                  onChange={(sortBy) => {
                    handleChange(index, sortBy);
                  }}
                  onRemove={(i) => {
                    if (sortList.length === 1) {
                      closePopover();
                    }
                    handleRemove(i);
                  }}
                />
              </Draggable>
            );
          })}
        </Container>
        <div className={`w-full ${sortList.length > 0 ? "pt-2 border-t border-gray-300" : ""}`}>
          <Popover className="w-full">
            <PopoverButton className="w-full flex items-center justify-between cursor-pointer outline-none">
              <div className="px-2 text-sm">
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  {t("Add sort")}
                </div>
              </div>
              <ChevronRight className="w-5 h-5" />
            </PopoverButton>
            <PopoverPanel
              anchor="right start"
              className="[--anchor-gap:20px] z-20 p-2 bg-white rounded-md border border-gray-300 shadow-xl outline-none"
            >
              {({ close }) => (
                <FilterDropdown
                  fields={availableFields}
                  isFilter={false}
                  onClick={(value) => {
                    handleAdd(value);
                  }}
                  onClose={close}
                />
              )}
            </PopoverPanel>
          </Popover>
        </div>
      </div>
    );
  };

  const renderTarget = () => {
    if (totalSorts === 0) {
      return (
        <>
          <div className="h-8 px-2 py-1.5 flex items-center space-x-2 text-sm font-medium text-gray-500">
            <ArrowDownNarrowWide className="h-4 w-4 text-gray-500" />
            <div className="pr-1">{t("Sort")}</div>
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          </div>
        </>
      );
    }
    return (
      <>
        <div className="h-8 px-2 py-1.5 flex items-center space-x-2 text-sm font-medium text-gray-500 bg-gray-100 rounded-l-md border-r border-t border-b border-gray-300">
          <ArrowDownNarrowWide className="h-4 w-4 text-gray-500" />
          <div className="pr-1">{t("Sorted by")}</div>
        </div>
        <div className="h-8 px-2 py-1.5 flex items-center space-x-2 text-sm font-medium">
          {totalSorts > 0 ? <div className="pr-2">{getFieldName()}</div> : null}
          {totalSorts > 1 ? (
            <div
              className="w-max px-1.5 py-0.5 rounded-full text-center text-xs text-white bg-gray-600"
              title={getFieldNames()}
            >
              +{totalSorts - 1}
            </div>
          ) : null}
          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
        </div>
      </>
    );
  };

  return (
    <div>
      <Popover>
        <PopoverButton className="inline-flex items-center space-x-2 rounded-md h-8 border border-gray-300 outline-none">
          {renderTarget()}
        </PopoverButton>
        <PopoverPanel
          anchor="bottom start"
          className="z-20 mt-2 p-2 bg-white rounded-md border border-gray-300 shadow-xl outline-none"
        >
          {({ close }) => renderPopoverPanel(close)}
        </PopoverPanel>
      </Popover>
    </div>
  );
};

export default SortBy;
